import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CheckCircle2, XCircle, Clock, User } from 'lucide-react';
import { useMudancaAprovacoes } from '@/hooks/useMudancaAprovacoes';
import { useAuth } from '@/hooks/useAuth';
import AprovacaoDialog from './AprovacaoDialog';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface MudancaAprovacaoPanelProps {
  mudancaId: string;
}

const MudancaAprovacaoPanel = ({ mudancaId }: MudancaAprovacaoPanelProps) => {
  const { aprovacoes, isLoading, aprovarMudanca, rejeitarMudanca } = useMudancaAprovacoes(mudancaId);
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTipo, setDialogTipo] = useState<'aprovar' | 'rejeitar'>('aprovar');
  const [aprovacaoSelecionada, setAprovacaoSelecionada] = useState<string | null>(null);

  const handleAprovarClick = (aprovacaoId: string) => {
    setAprovacaoSelecionada(aprovacaoId);
    setDialogTipo('aprovar');
    setDialogOpen(true);
  };

  const handleRejeitarClick = (aprovacaoId: string) => {
    setAprovacaoSelecionada(aprovacaoId);
    setDialogTipo('rejeitar');
    setDialogOpen(true);
  };

  const handleConfirmAcao = (justificativa: string) => {
    if (!aprovacaoSelecionada) return;

    if (dialogTipo === 'aprovar') {
      aprovarMudanca.mutate(
        { 
          aprovacaoId: aprovacaoSelecionada, 
          justificativa,
          mudancaId 
        },
        {
          onSuccess: () => {
            setDialogOpen(false);
            setAprovacaoSelecionada(null);
          },
        }
      );
    } else {
      rejeitarMudanca.mutate(
        { 
          aprovacaoId: aprovacaoSelecionada, 
          justificativa,
          mudancaId 
        },
        {
          onSuccess: () => {
            setDialogOpen(false);
            setAprovacaoSelecionada(null);
          },
        }
      );
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'aprovada':
        return (
          <Badge variant="default" className="bg-green-600">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Aprovada
          </Badge>
        );
      case 'rejeitada':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            Rejeitada
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary">
            <Clock className="h-3 w-3 mr-1" />
            Pendente
          </Badge>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-sm text-muted-foreground">Carregando aprovações...</div>
      </div>
    );
  }

  if (aprovacoes.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-sm text-muted-foreground">
            Esta mudança foi pré-aprovada automaticamente (tipo: Padrão)
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Aprovações da Mudança</CardTitle>
          <CardDescription>
            Todas as aprovações devem ser concluídas para execução da mudança
          </CardDescription>
        </CardHeader>
      </Card>

      {aprovacoes.map((aprovacao) => {
        const isAprovadorLogado = user?.id === aprovacao.aprovador_id;
        const isPendente = aprovacao.status_aprovacao === 'pendente';
        const podeAgir = isAprovadorLogado && isPendente;

        return (
          <Card key={aprovacao.id}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={aprovacao.aprovador?.avatar_url} />
                  <AvatarFallback>
                    <User className="h-6 w-6" />
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{aprovacao.aprovador?.name || 'Aprovador não encontrado'}</p>
                      <p className="text-sm text-muted-foreground">
                        Nível {aprovacao.nivel_aprovacao} • {aprovacao.aprovador?.email}
                      </p>
                    </div>
                    {getStatusBadge(aprovacao.status_aprovacao)}
                  </div>

                  {aprovacao.status_aprovacao !== 'pendente' && (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {aprovacao.status_aprovacao === 'aprovada' ? 'Aprovada' : 'Rejeitada'}{' '}
                        {aprovacao.data_aprovacao && 
                          formatDistanceToNow(new Date(aprovacao.data_aprovacao), {
                            addSuffix: true,
                            locale: ptBR,
                          })
                        }
                      </p>
                      {aprovacao.justificativa && (
                        <div className="p-3 bg-muted rounded-md">
                          <p className="text-sm font-medium mb-1">Justificativa:</p>
                          <p className="text-sm">{aprovacao.justificativa}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {podeAgir && (
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        onClick={() => handleAprovarClick(aprovacao.id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Aprovar
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleRejeitarClick(aprovacao.id)}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Rejeitar
                      </Button>
                    </div>
                  )}

                  {isPendente && !isAprovadorLogado && (
                    <p className="text-sm text-muted-foreground">
                      ⏳ Aguardando aprovação do responsável
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      <AprovacaoDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        tipo={dialogTipo}
        onConfirm={handleConfirmAcao}
        isPending={aprovarMudanca.isPending || rejeitarMudanca.isPending}
      />
    </div>
  );
};

export default MudancaAprovacaoPanel;
